import {useEffect, useRef, useState} from "react";
import {useStore} from "react-redux";
import {useGoogleOneTapLogin} from "@react-oauth/google";

import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {IRootState} from "../../store/store";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {ViewType} from "../../view_type/ViewType";
import {IPostAuthFlowContext} from "../actions/auth_succeeded_event";
import {reportAuthSuccess} from "../actions/report_auth_success";
import {googleOneTapLogin} from "../api/google_one_tap_login";
import {isRegistrationWizardFlowActive, isRegistrationWizardRunActive, IStartRegistrationFlowPayload} from "../state/registration_flow_slice";
import {AmplitudeAuthLocation, getAmplitudeAuthLocation, trackAmplitudeAuthEvent} from "../tracking/track_amplitude_auth_event";
import {AuthActionType} from "../types/AuthActionType";
import {getAmplitudeFlowTracking} from "../utils/get_amplitude_flow_tracking";

interface IProps {
    disable?: boolean;
    gtmSourceSection?: string;
    userSource?: UserSource;
}

const ONE_TAP_DELAY_MS = 20000;

type RegistrationFlowState = IRootState["registrationFlow"];
type PropertyDetail = IRootState["property"]["property"];

interface IGoogleOneTapAuthTracking {
    isWizardFlow: boolean;
    placement: AmplitudeAuthPlacement | null;
    location: AmplitudeAuthLocation;
    authPlacement?: AmplitudeAuthPlacement | null;
}

const getGoogleOneTapAuthTracking = (registrationFlow: RegistrationFlowState, pageLocation: AmplitudeAuthLocation): IGoogleOneTapAuthTracking => {
    if (!isRegistrationWizardRunActive(registrationFlow)) {
        return {isWizardFlow: false, placement: "google_popup", location: pageLocation};
    }

    const {placement, location} = getAmplitudeFlowTracking(registrationFlow, pageLocation);

    return {isWizardFlow: true, placement, location, authPlacement: placement};
};

const snapshotRegistrationFlow = (flow: RegistrationFlowState): IStartRegistrationFlowPayload => ({
    isUserLoggedIn: flow.isUserLoggedIn,
    hasCompleteProfile: flow.hasCompleteProfile,
    hasPhoneVerified: flow.hasPhoneVerified,
    registrationMethod: flow.registrationMethod,
    socialMediaType: flow.socialMediaType,
    userSource: flow.userSource,
    gtmSourceSection: flow.gtmSourceSection,
    gtmClickText: flow.gtmClickText,
    modalVariant: flow.modalVariant,
    propertyId: flow.propertyId,
    offerId: flow.offerId,
    vendorId: flow.vendorId,
    titleVariant: flow.titleVariant,
    showLoginForm: flow.showLoginForm,
    registrationSuccessCallback: flow.registrationSuccessCallback,
    useLeadAfterRegistration: flow.useLeadAfterRegistration,
    leadSource: flow.leadSource,
    leadSourceSection: flow.leadSourceSection,
    amplitudePlacement: flow.amplitudePlacement,
    amplitudeLocation: flow.amplitudeLocation
});

export const getGoogleOneTapPostAuthFlowContext = (
    registrationFlow: RegistrationFlowState,
    viewType: ViewType | null,
    property: PropertyDetail
): IPostAuthFlowContext | undefined => {
    if (registrationFlow.isActive) {
        return {
            flowWasActive: true,
            currentStep: registrationFlow.currentStep,
            registrationFlow: snapshotRegistrationFlow(registrationFlow)
        };
    }

    if (viewType !== ViewType.PROPERTY || !property?.id || !property.offer.id || !property.offer.vendor.id) {
        return undefined;
    }

    return {
        flowWasActive: false,
        registrationFlow: {
            userSource: UserSource.REGISTRATION_GOOGLE_ONE_TAP_RP,
            modalVariant: "hidden_prices_variant_b",
            propertyId: property.id,
            offerId: property.offer.id,
            vendorId: property.offer.vendor.id,
            useLeadAfterRegistration: true,
            leadSource: ApplicationSource.PropertyRevealPrice,
            leadSourceSection: ApplicationSourceSection.MODAL
        }
    };
};

export const useGoogleOneTap = (props: IProps = {}) => {
    const isAuthenticated = useIsAuthenticated();
    const dispatch = useAppDispatch();
    const store = useStore<IRootState>();
    const registrationFlow = useAppSelector((state) => state.registrationFlow);
    const isWizardFlowActive = isRegistrationWizardFlowActive(registrationFlow);
    const viewType = useAppSelector((state) => state.viewType.current);
    const property = useAppSelector((state) => state.property.property);
    const amplitudeLocation = getAmplitudeAuthLocation(viewType);
    const [readyToShow, setReadyToShow] = useState(false);
    const loginInFlightRef = useRef(false);

    useEffect(() => {
        const timer = setTimeout(() => setReadyToShow(true), ONE_TAP_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    const onOneTapLoginSuccess = async (isNewUser: boolean, postAuthFlowContext?: IPostAuthFlowContext) => {
        const userSource = props.userSource ?? UserSource.REGISTRATION_GOOGLE_ONE_TAP_RP;
        const gtmSourceSection = postAuthFlowContext?.registrationFlow.gtmSourceSection ?? props.gtmSourceSection;

        const {placement, location, authPlacement} = getGoogleOneTapAuthTracking(store.getState().registrationFlow, amplitudeLocation);

        reportAuthSuccess(
            dispatch,
            {
                method: "google_one_tap",
                authActionType: isNewUser ? AuthActionType.REGISTRATION : AuthActionType.LOGIN,
                userSource,
                gtmSourceSection,
                placement,
                location,
                authPlacement
            },
            {registrationMethod: "social", socialMediaType: "google", authOrigin: "google_one_tap", postAuthFlowContext}
        );
    };

    const googleOnOneTapLoginError = (error?: unknown) => {
        loginInFlightRef.current = false;
        notifyBugsnag({message: `One tap login error`}, `third-party-login error`, {originalError: error});

        dispatch(addFailureNotification("Coś poszło nie tak. Spróbuj później."));
    };

    useGoogleOneTapLogin({
        onSuccess: async (response) => {
            if (!response.credential || loginInFlightRef.current) {
                return;
            }

            loginInFlightRef.current = true;
            const clickTracking = getGoogleOneTapAuthTracking(store.getState().registrationFlow, amplitudeLocation);

            if (!clickTracking.isWizardFlow) {
                trackAmplitudeAuthEvent("login_click", {
                    loginMethod: "google_popup",
                    loginPlacement: clickTracking.placement,
                    location: clickTracking.location
                });
            }

            const postAuthFlowContext = getGoogleOneTapPostAuthFlowContext(registrationFlow, viewType, property);

            try {
                await googleOneTapLogin({
                    credential: response.credential,
                    onSuccess: (isNewUser) => onOneTapLoginSuccess(isNewUser, postAuthFlowContext),
                    onError: googleOnOneTapLoginError
                });
            } catch (error) {
                googleOnOneTapLoginError(error);
            }
        },
        onError: () => {
            googleOnOneTapLoginError("useGoogleOneTapLogin error, login in failure");
        },
        disabled: isAuthenticated || props.disable || !readyToShow || isWizardFlowActive
    });
};
import {useEffect, useRef, useState} from "react";
import {useStore} from "react-redux";
import {useGoogleOneTapLogin} from "@react-oauth/google";

import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {IRootState} from "../../store/store";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {ViewType} from "../../view_type/ViewType";
import {IPostAuthFlowContext} from "../actions/auth_succeeded_event";
import {reportAuthSuccess} from "../actions/report_auth_success";
import {googleOneTapLogin} from "../api/google_one_tap_login";
import {isRegistrationWizardFlowActive, isRegistrationWizardRunActive, IStartRegistrationFlowPayload} from "../state/registration_flow_slice";
import {AmplitudeAuthLocation, getAmplitudeAuthLocation, trackAmplitudeAuthEvent} from "../tracking/track_amplitude_auth_event";
import {AuthActionType} from "../types/AuthActionType";
import {getAmplitudeFlowTracking} from "../utils/get_amplitude_flow_tracking";

interface IProps {
    disable?: boolean;
    gtmSourceSection?: string;
    userSource?: UserSource;
}

const ONE_TAP_DELAY_MS = 20000;

type RegistrationFlowState = IRootState["registrationFlow"];
type PropertyDetail = IRootState["property"]["property"];

interface IGoogleOneTapAuthTracking {
    isWizardFlow: boolean;
    placement: AmplitudeAuthPlacement | null;
    location: AmplitudeAuthLocation;
    authPlacement?: AmplitudeAuthPlacement | null;
}

const getGoogleOneTapAuthTracking = (registrationFlow: RegistrationFlowState, pageLocation: AmplitudeAuthLocation): IGoogleOneTapAuthTracking => {
    if (!isRegistrationWizardRunActive(registrationFlow)) {
        return {isWizardFlow: false, placement: "google_popup", location: pageLocation};
    }

    const {placement, location} = getAmplitudeFlowTracking(registrationFlow, pageLocation);

    return {isWizardFlow: true, placement, location, authPlacement: placement};
};

const snapshotRegistrationFlow = (flow: RegistrationFlowState): IStartRegistrationFlowPayload => ({
    isUserLoggedIn: flow.isUserLoggedIn,
    hasCompleteProfile: flow.hasCompleteProfile,
    hasPhoneVerified: flow.hasPhoneVerified,
    registrationMethod: flow.registrationMethod,
    socialMediaType: flow.socialMediaType,
    userSource: flow.userSource,
    gtmSourceSection: flow.gtmSourceSection,
    gtmClickText: flow.gtmClickText,
    modalVariant: flow.modalVariant,
    propertyId: flow.propertyId,
    offerId: flow.offerId,
    vendorId: flow.vendorId,
    titleVariant: flow.titleVariant,
    showLoginForm: flow.showLoginForm,
    registrationSuccessCallback: flow.registrationSuccessCallback,
    useLeadAfterRegistration: flow.useLeadAfterRegistration,
    leadSource: flow.leadSource,
    leadSourceSection: flow.leadSourceSection,
    amplitudePlacement: flow.amplitudePlacement,
    amplitudeLocation: flow.amplitudeLocation
});

export const getGoogleOneTapPostAuthFlowContext = (
    registrationFlow: RegistrationFlowState,
    viewType: ViewType | null,
    property: PropertyDetail
): IPostAuthFlowContext | undefined => {
    if (registrationFlow.isActive) {
        return {
            flowWasActive: true,
            currentStep: registrationFlow.currentStep,
            registrationFlow: snapshotRegistrationFlow(registrationFlow)
        };
    }

    if (viewType !== ViewType.PROPERTY || !property?.id || !property.offer.id || !property.offer.vendor.id) {
        return undefined;
    }

    return {
        flowWasActive: false,
        registrationFlow: {
            userSource: UserSource.REGISTRATION_GOOGLE_ONE_TAP_RP,
            modalVariant: "hidden_prices_variant_b",
            propertyId: property.id,
            offerId: property.offer.id,
            vendorId: property.offer.vendor.id,
            useLeadAfterRegistration: true,
            leadSource: ApplicationSource.PropertyRevealPrice,
            leadSourceSection: ApplicationSourceSection.MODAL
        }
    };
};

export const useGoogleOneTap = (props: IProps = {}) => {
    const isAuthenticated = useIsAuthenticated();
    const dispatch = useAppDispatch();
    const store = useStore<IRootState>();
    const registrationFlow = useAppSelector((state) => state.registrationFlow);
    const isWizardFlowActive = isRegistrationWizardFlowActive(registrationFlow);
    const viewType = useAppSelector((state) => state.viewType.current);
    const property = useAppSelector((state) => state.property.property);
    const amplitudeLocation = getAmplitudeAuthLocation(viewType);
    const [readyToShow, setReadyToShow] = useState(false);
    const loginInFlightRef = useRef(false);

    useEffect(() => {
        const timer = setTimeout(() => setReadyToShow(true), ONE_TAP_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    const onOneTapLoginSuccess = async (isNewUser: boolean, postAuthFlowContext?: IPostAuthFlowContext) => {
        const userSource = props.userSource ?? UserSource.REGISTRATION_GOOGLE_ONE_TAP_RP;
        const gtmSourceSection = postAuthFlowContext?.registrationFlow.gtmSourceSection ?? props.gtmSourceSection;

        const {placement, location, authPlacement} = getGoogleOneTapAuthTracking(store.getState().registrationFlow, amplitudeLocation);

        reportAuthSuccess(
            dispatch,
            {
                method: "google_one_tap",
                authActionType: isNewUser ? AuthActionType.REGISTRATION : AuthActionType.LOGIN,
                userSource,
                gtmSourceSection,
                placement,
                location,
                authPlacement
            },
            {registrationMethod: "social", socialMediaType: "google", authOrigin: "google_one_tap", postAuthFlowContext}
        );
    };

    const googleOnOneTapLoginError = (error?: unknown) => {
        loginInFlightRef.current = false;
        notifyBugsnag({message: `One tap login error`}, `third-party-login error`, {originalError: error});

        dispatch(addFailureNotification("Coś poszło nie tak. Spróbuj później."));
    };

    useGoogleOneTapLogin({
        onSuccess: async (response) => {
            if (!response.credential || loginInFlightRef.current) {
                return;
            }

            loginInFlightRef.current = true;
            const clickTracking = getGoogleOneTapAuthTracking(store.getState().registrationFlow, amplitudeLocation);

            if (!clickTracking.isWizardFlow) {
                trackAmplitudeAuthEvent("login_click", {
                    loginMethod: "google_popup",
                    loginPlacement: clickTracking.placement,
                    location: clickTracking.location
                });
            }

            const postAuthFlowContext = getGoogleOneTapPostAuthFlowContext(registrationFlow, viewType, property);

            try {
                await googleOneTapLogin({
                    credential: response.credential,
                    onSuccess: (isNewUser) => onOneTapLoginSuccess(isNewUser, postAuthFlowContext),
                    onError: googleOnOneTapLoginError
                });
            } catch (error) {
                googleOnOneTapLoginError(error);
            }
        },
        onError: () => {
            googleOnOneTapLoginError("useGoogleOneTapLogin error, login in failure");
        },
        disabled: isAuthenticated || props.disable || !readyToShow || isWizardFlowActive
    });
};
